import React, { useState } from 'react';
import {
    Box,
    Typography,
    Stack,
    Chip,
    Grid,
    Alert,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    Snackbar,
    Paper,
    Avatar,
    IconButton,
    Tooltip,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    Divider
} from '@mui/material';
import {
    School as CourseIcon,
    Person as StudentIcon,
    Topic as TopicIcon,
    Quiz as QuestionIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    CalendarToday as CalendarIcon,
    PersonRemove as RemoveStudentIcon,
    PersonAdd as AddStudentIcon,
    Group as EnrollmentIcon
} from '@mui/icons-material';

const CourseDetails = ({ selectedCourse, onCourseUpdate, apiClient }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [editData, setEditData] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [courseDetails, setCourseDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Academic levels
    const academicLevels = [
        'bachelor_1', 'bachelor_2', 'bachelor_3',
        'master_1', 'master_2', 'master_3'
    ];

    // Initialize edit data when entering edit mode
    const handleStartEdit = () => {
        setEditData({
            title: selectedCourse?.title || '',
            description: selectedCourse?.description || '',
            academic_level: selectedCourse?.academic_level || 'bachelor_3'
        });
        setIsEditing(true);
    };

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Save changes
    const handleSave = async () => {
        if (!apiClient || !selectedCourse) return;

        setIsLoading(true);
        try {
            const updateData = {
                title: editData.title,
                description: editData.description,
                academic_level: editData.academic_level
            };

            const updatedCourse = await apiClient.put(`/api/courses/${selectedCourse.id}`, updateData);
            
            // Notify parent component of the update
            if (onCourseUpdate) {
                onCourseUpdate(updatedCourse);
            }

            setSnackbar({ open: true, message: 'Course updated successfully!', severity: 'success' });
            setIsEditing(false);
        } catch (error) {
            console.error('Failed to update course:', error);
            setSnackbar({ 
                open: true, 
                message: error.message || 'Failed to update course', 
                severity: 'error' 
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Cancel editing
    const handleCancel = () => {
        setEditData({});
        setIsEditing(false);
    };

    // Close snackbar
    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Fetch detailed course information
    const fetchCourseDetails = async () => {
        if (!apiClient || !selectedCourse || courseDetails) return;

        setLoadingDetails(true);
        try {
            const response = await apiClient.get(`/api/courses/${selectedCourse.id}`);
            setCourseDetails(response);
        } catch (error) {
            console.error('Failed to fetch course details:', error);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Load course details when component mounts
    React.useEffect(() => {
        fetchCourseDetails();
    }, [selectedCourse]);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = new Date(timestamp);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'N/A';
        }
    };

    // Format academic level for display
    const formatAcademicLevel = (level) => {
        if (!level) return 'Unknown';
        const parts = level.split('_');
        if (parts.length === 2) {
            return `${parts[0].charAt(0).toUpperCase() + parts[0].slice(1)} ${parts[1]}`;
        }
        return level;
    };

    if (!selectedCourse) {
        return (
            <Box sx={{p: 2, textAlign: 'center'}}>
                <Avatar sx={{width: 64, height: 64, mx: 'auto', mb: 2}}>
                    <CourseIcon sx={{fontSize: 32}}/>
                </Avatar>
                <Typography variant="h6" gutterBottom>
                    No course selected
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Select a course from the list to view its details
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{p: 2, height: '100%', overflow: 'auto'}}>
            {/* Header Section */}
            <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2}}>
                {!isEditing ? (
                    <Tooltip title="Edit Course">
                        <IconButton onClick={handleStartEdit} color="primary">
                            <EditIcon/>
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<CancelIcon/>}
                            onClick={handleCancel}
                            size="small"
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={isLoading ? <CircularProgress size={16} color="inherit"/> : <SaveIcon/>}
                            onClick={handleSave}
                            size="small"
                            disabled={isLoading}
                        >
                            Save
                        </Button>
                    </Stack>
                )}
            </Box>

            {/* Main Content */}
            <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid size={{xs: 12}}>
                    <Paper variant="outlined" sx={{p: 3}}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                            <Avatar>
                                <CourseIcon/>
                            </Avatar>
                            <Typography variant="h6">
                                Course Information
                            </Typography>
                        </Stack>

                        <Grid container spacing={3}>
                            <Grid size={{xs: 12, md: 6}}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Course Title
                                    </Typography>
                                    {isEditing ? (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={editData.title}
                                            onChange={(e) => handleFieldChange('title', e.target.value)}
                                            placeholder="Enter course title"
                                        />
                                    ) : (
                                        <Typography variant="body1">
                                            {selectedCourse.title}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>

                            <Grid size={{xs: 12, md: 6}}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Academic Level
                                    </Typography>
                                    {isEditing ? (
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={editData.academic_level}
                                                onChange={(e) => handleFieldChange('academic_level', e.target.value)}
                                            >
                                                {academicLevels.map((level) => (
                                                    <MenuItem key={level} value={level}>
                                                        {formatAcademicLevel(level)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Chip 
                                            label={formatAcademicLevel(selectedCourse.academic_level)} 
                                            color="primary" 
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                            </Grid>

                            <Grid size={{xs: 12}}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Description
                                    </Typography>
                                    {isEditing ? (
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            size="small"
                                            value={editData.description}
                                            onChange={(e) => handleFieldChange('description', e.target.value)}
                                            placeholder="Enter course description"
                                        />
                                    ) : (
                                        <Typography variant="body1">
                                            {selectedCourse.description || 'No description provided'}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>

                            <Grid size={{xs: 12, md: 6}}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Teacher
                                    </Typography>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                        <Avatar sx={{width: 32, height: 32}}>
                                            {selectedCourse.teacher?.username?.charAt(0)?.toUpperCase() || 'T'}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2">
                                                {selectedCourse.teacher?.username || 'Unknown Teacher'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {selectedCourse.teacher?.email || ''}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid size={{xs: 12, md: 6}}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Course Statistics
                                    </Typography>
                                    <Stack direction="row" spacing={2}>
                                        <Chip 
                                            icon={<StudentIcon/>}
                                            label={`${selectedCourse.student_count} Students`} 
                                            color="success" 
                                            variant="outlined"
                                            size="small"
                                        />
                                        <Chip 
                                            icon={<TopicIcon/>}
                                            label={`${selectedCourse.topic_count} Topics`} 
                                            color="info" 
                                            variant="outlined"
                                            size="small"
                                        />
                                        <Chip 
                                            icon={<QuestionIcon/>}
                                            label={`${selectedCourse.question_count} Questions`} 
                                            color="warning" 
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Stack>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Enrollment Information */}
                {courseDetails && (
                    <Grid size={{xs: 12}}>
                        <Paper variant="outlined" sx={{p: 3}}>
                            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                <Avatar>
                                    <EnrollmentIcon/>
                                </Avatar>
                                <Typography variant="h6">
                                    Enrolled Students ({courseDetails.students?.length || 0})
                                </Typography>
                            </Stack>

                            {loadingDetails ? (
                                <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
                                    <CircularProgress size={24}/>
                                </Box>
                            ) : courseDetails.students && courseDetails.students.length > 0 ? (
                                <List dense>
                                    {courseDetails.students.map((student, index) => (
                                        <React.Fragment key={student.id}>
                                            <ListItem>
                                                <ListItemAvatar>
                                                    <Avatar sx={{width: 32, height: 32}}>
                                                        {student.username?.charAt(0)?.toUpperCase() || 'S'}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={student.username || 'Unknown Student'}
                                                    secondary={student.email}
                                                />
                                                <ListItemSecondaryAction>
                                                    <Tooltip title="Remove Student">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => console.log('Remove student', student.id)}
                                                        >
                                                            <RemoveStudentIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            {index < courseDetails.students.length - 1 && <Divider/>}
                                        </React.Fragment>
                                    ))}
                                </List>
                            ) : (
                                <Alert severity="info">
                                    No students are currently enrolled in this course.
                                </Alert>
                            )}
                        </Paper>
                    </Grid>
                )}

                {/* Timestamps */}
                <Grid size={{xs: 12}}>
                    <Paper variant="outlined" sx={{p: 3}}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                            <Avatar>
                                <CalendarIcon/>
                            </Avatar>
                            <Typography variant="h6">
                                Course Timeline
                            </Typography>
                        </Stack>

                        <Grid container spacing={2}>
                            <Grid size={{xs: 12, md: 6}}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Created
                                </Typography>
                                <Typography variant="body2">
                                    {formatTimestamp(selectedCourse.created_at)}
                                </Typography>
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Last Updated
                                </Typography>
                                <Typography variant="body2">
                                    {formatTimestamp(selectedCourse.updated_at)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{width: '100%'}}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CourseDetails;